import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useCanonicalUrlWithPath, MARKETING_ROUTES } from './seoUtils';

const StructuredData: React.FC = () => {
	const location = useLocation();

	// Only marketing pages get structured data
	const shouldRenderStructuredData = MARKETING_ROUTES.includes(location.pathname);

	if (!shouldRenderStructuredData) {
		return null;
	}

	// Get canonical URL using shared hook
	const { base, canonicalUrl } = useCanonicalUrlWithPath();

	// Get page title and description based on route
	const getPageInfo = (pathname: string) => {
		switch (pathname) {
			case '/':
				return {
					name: 'Home',
					description: 'The AI platform measuring how people collaborate, influence, and navigate challenges with immersive AI scenarios.'
				};
			case '/about-us':
				return {
					name: 'About Us',
					description: 'Learn about Simsola and our mission to revolutionize AI training through immersive scenarios for real-world practice.'
				};
			case '/book-a-demo':
				return {
					name: 'Book a Demo',
					description: 'Schedule a personalized demonstration of Simsola\'s AI training platform and see how it can transform your leadership development.'
				};
			case '/contact-us':
				return {
					name: 'Contact Us',
					description: 'Get in touch with the Simsola team to learn more about our AI training solutions and how we can help your organization.'
				};
			case '/news/tmn-choice-awards-2026':
				return {
					name: 'Simsola Wins 2026 Training Magazine Network Choice Award',
					description: 'Training Magazine Network members voted Simsola a winner in the Immersive & Spatial Learning Technologies category of the 2026 Training Magazine Network Choice Awards.'
				};
			case '/product':
				return {
					name: 'Product',
					description: 'Discover Simsola\'s comprehensive AI training platform featuring immersive scenarios for leadership development and collaboration.'
				};
			case '/product/6d-assessment':
				return {
					name: 'Simsola 6D Assessment',
					description: 'Evaluate leadership across Influence, Curiosity, Courage, Humility, Empathy, and Collaboration with Simsola’s 6D Assessment.'
				};
			case '/resources':
				return {
					name: 'Resources',
					description: 'Access guides, templates, blogs, workshops, and events to maximize your AI training experience with Simsola.'
				};
			case '/results':
				return {
					name: 'Results',
					description: 'See the measurable impact of Simsola\'s AI training platform on leadership development and organizational performance.'
				};
			case '/trust-center':
				return {
					name: 'Trust Center',
					description: 'Learn about Simsola\'s commitment to security, privacy, and compliance in our AI training platform.'
				};
			case '/vision':
				return {
					name: 'Vision',
					description: 'Explore Simsola\'s vision for the future of AI-powered leadership training and immersive learning experiences.'
				};
			default:
				return {
					name: 'Page',
					description: 'Simsola - AI training platform for leadership development through immersive scenarios.'
				};
		}
	};

	const pageInfo = getPageInfo(location.pathname);


	// Dynamic logo URL with fallback
	const getLogoUrl = () => {
    const logoPath = process.env.REACT_APP_LOGO_PATH || '/favicon.ico';
		return `${base}${logoPath}`;
	};

  // Organization Schema
	const organizationSchema = {
		"@context": "https://schema.org",
		"@type": "Organization",
		"name": "Simsola Ai",
		"url": base,
		"logo": getLogoUrl(),
		"description": "The AI platform measuring how people collaborate, influence, and navigate challenges with immersive AI scenarios for real world practice."
	};

	// WebSite Schema
	const websiteSchema = {
		"@context": "https://schema.org",
		"@type": "WebSite",
		"name": "Simsola Ai",
		"url": base,
		"description": "AI training platform for leadership development through immersive scenarios",
		"publisher": {
			"@type": "Organization",
			"name": "Simsola Ai"
		}
	};

	// WebPage Schema
	const webpageSchema = {
		"@context": "https://schema.org",
		"@type": "WebPage",
		"name": `Simsola Ai - ${pageInfo.name}`,
		"description": pageInfo.description,
		"url": canonicalUrl,
		"isPartOf": {
			"@type": "WebSite",
			"name": "Simsola Ai",
			"url": base
		},
		"publisher": {
			"@type": "Organization",
			"name": "Simsola Ai"
		},
		"breadcrumb": {
			"@type": "BreadcrumbList",
			"itemListElement": [
				{
					"@type": "ListItem",
					"position": 1,
					"name": "Home",
					"item": base
				},
				...(location.pathname !== '/' ? [{
					"@type": "ListItem",
					"position": 2,
					"name": pageInfo.name,
					"item": canonicalUrl
				}] : [])
			]
		}
	};

	return (
		<Helmet>
			<script type="application/ld+json">
				{JSON.stringify(organizationSchema)}
			</script>
			<script type="application/ld+json">
				{JSON.stringify(websiteSchema)}
			</script>
			<script type="application/ld+json">
				{JSON.stringify(webpageSchema)}
			</script>
		</Helmet>
	);
};

export default StructuredData;
